import { sendMessage, getMessages, getStatus, getChatId, getConversationId, configureChatId, hydrateMessages, onMessagesUpdated, onStatusUpdated, onError, stop, appendAssistantMessage, isAwaitingToolContinuation } from "./chat-runtime.js";
import { addCharliAttribution } from "./charli-link-attribution.js";
import { processCharliHits } from "./charli-result-classification.js";
import { submitCharliTiming } from "./charli-performance.js";
import { PREPARING_LABEL, STILL_WORKING_LABEL, WORKING_LABEL } from "./charli-progress.js";
import {
  createRequestId,
  createRequestLifecycle,
  hasRequiredChatConfig,
  isRequestActive,
  shouldQueueInitialObserverQuery,
  transitionRequestLifecycle,
} from "./charli-request-lifecycle.js";
import {
  createDesiredRows,
  getResultCardReveal,
  getResultSectionConfig,
  hasDisplayableContent,
  planResultCardReconciliation,
  planRowReconciliation,
  shouldAnimateResultCards,
  shouldShowResults,
  nextRevealLength,
  toStreamingDisplayText,
} from "./charli-conversation-renderer.js";
import {
  CHARLI_SESSION_KEY,
  createSessionSnapshot,
  getStandaloneSessionPlan,
  interruptSessionMessages,
  readSessionSnapshot,
  writeSessionSnapshot,
} from "./charli-session.js";

const CHAMFR_PINK = "#e11d73";

const WAIT_MESSAGES = [
  "Chamfr stocks thousands of ready-to-ship catheters, balloons, heat shrink, hypotubes, and sterile packaging.",
  "Need a build partner? Chamfr's supplier directory covers laser processing, catheter assembly, RF welding, and coatings.",
  "Shop transparent pricing from OEM-trusted brands for rapid pilot production.",
];

let waitTimer = null;
let waitIndex = 0;
let unloadStopWired = false;
let renderRafId = 0;
let renderDirty = false;
let conversationPinnedToBottom = true;
// Once an answer starts, stop following new content so readers are not pulled to the bottom.
let autoScrollSuppressed = false;
let anchoredAnswerRequestId = null;
// The question row to follow up to the top of the conversation while its answer streams.
let answerAnchorElement = null;
let answerAnchorRafId = 0;
let conversationScrollRafId = 0;
let conversationScrollForce = false;
let conversationResizeObserver = null;
let initialized = false;
let activeRequestLifecycle = null;
// Request whose first response ended on a browser tool call; it completes when the SDK's follow-up finishes.
let awaitingContinuationRequestId = null;
let renderedConversationRows = new Map();
const feedbackStateByMessageId = new Map();
const CHAMFR_CHARLI_BOOTSTRAP_KEY = "__chamfrCharliBootstrapStarted";
const CHAMFR_CHARLI_HISTORY_KEY = "chamfrConversationId";
const CONVERSATION_BOTTOM_THRESHOLD = 64;
let standaloneSessionQuery = "";
let standaloneSessionEnabled = false;
let standaloneSessionWritable = false;
let standaloneConversationId = "";

if (!window[CHAMFR_CHARLI_BOOTSTRAP_KEY]) {
  window[CHAMFR_CHARLI_BOOTSTRAP_KEY] = true;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", waitForConfig);
  } else {
    waitForConfig();
  }
}

function waitForConfig() {
  if (hasRequiredChatConfig(window.algolia)) {
    init();
  } else {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (hasRequiredChatConfig(window.algolia)) {
        clearInterval(interval);
        init();
      } else if (attempts > 50) {
        clearInterval(interval);
      }
    }, 100);
  }
}

function init() {
  if (initialized) return;
  initialized = true;
  injectStyles();
  wireStopOnUnload();

  if (window.chamfrAiChatAutoOpen === true) {
    standaloneSessionEnabled = true;
    standaloneSessionQuery = getFailedQuery().trim();
    standaloneConversationId = ensureStandaloneConversationId();
    configureChatId(standaloneConversationId);
    const restoreResult = restoreStandaloneSession(standaloneSessionQuery, standaloneConversationId);
    const sessionPlan = getStandaloneSessionPlan(restoreResult, getNavigationType());
    standaloneSessionWritable = sessionPlan.writable;
    setupAssistant(standaloneSessionQuery, {
      prefillInitialQuery: sessionPlan.prefill,
      suppressInitialSend: !sessionPlan.autoSend,
    });
    observeNoResults();
    return;
  }

  if (isNoResultsPage()) {
    renderAssistantCta(getFailedQuery());
  }
  observeNoResults();
}

function wireStopOnUnload() {
  if (unloadStopWired) return;
  unloadStopWired = true;

  const stopIfBusy = () => {
    if (interruptActiveRequest()) return;
    persistStandaloneSession();
    const s = getStatus();
    if (s === "streaming" || s === "submitted") stop();
  };

  window.addEventListener("pagehide", stopIfBusy, { capture: true });
  window.addEventListener("beforeunload", stopIfBusy, { capture: true });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) reconcileStandaloneHistoryEntry();
  }, { capture: true });
}

function getNavigationType() {
  return window.performance?.getEntriesByType?.("navigation")?.[0]?.type || "navigate";
}

function ensureStandaloneConversationId() {
  const currentState = window.history.state;
  const existingId = currentState?.[CHAMFR_CHARLI_HISTORY_KEY];
  if (typeof existingId === "string" && existingId) return existingId;
  const conversationId = crypto.randomUUID?.() || `charli_conversation_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const nextState = currentState && typeof currentState === "object"
    ? { ...currentState, [CHAMFR_CHARLI_HISTORY_KEY]: conversationId }
    : { [CHAMFR_CHARLI_HISTORY_KEY]: conversationId };
  window.history.replaceState(nextState, "");
  return conversationId;
}

function interruptActiveRequest() {
  if (!isRequestActive(activeRequestLifecycle)) return false;
  const requestId = activeRequestLifecycle.requestId;
  const currentAssistantMessageId = activeRequestLifecycle.assistantMessageId;
  const interruptedAssistantMessageId = currentAssistantMessageId || `charli-interrupted-${requestId}`;
  activeRequestLifecycle = {
    ...transitionRequestLifecycle(activeRequestLifecycle, {
      type: "interrupt",
      requestId,
      occurredAtMs: performance.now(),
    }),
    assistantMessageId: interruptedAssistantMessageId,
  };
  submitActiveRequestTiming();
  hydrateMessages(interruptSessionMessages(
    getMessages(),
    currentAssistantMessageId,
    interruptedAssistantMessageId,
  ));
  stop();
  updateAssistantControls();
  persistStandaloneSession();
  scheduleRenderConversation();
  return true;
}

function restoreStandaloneSession(query, conversationId) {
  let restored;
  try {
    restored = readSessionSnapshot(window.sessionStorage, CHARLI_SESSION_KEY, conversationId, query);
  } catch {
    return { restored: false, reason: "storage-error", messages: [] };
  }
  if (restored.restored) hydrateMessages(restored.messages);
  return restored;
}

function reconcileStandaloneHistoryEntry() {
  if (!standaloneSessionEnabled) return;
  activeRequestLifecycle = null;
  standaloneConversationId = ensureStandaloneConversationId();
  const restoreResult = restoreStandaloneSession(standaloneSessionQuery, standaloneConversationId);
  standaloneSessionWritable = restoreResult.restored;
  if (!restoreResult.restored) hydrateMessages([]);
  const input = document.getElementById("chamfr-input");
  if (input) {
    input.value = restoreResult.restored ? "" : standaloneSessionQuery;
    autosizeChatInput(input);
  }
  updateAssistantControls();
  scheduleRenderConversation();
}

function persistStandaloneSession() {
  if (!standaloneSessionEnabled || !standaloneSessionWritable) return;
  try {
    const snapshot = createSessionSnapshot(
      standaloneSessionQuery,
      getMessages(),
      isRequestActive(activeRequestLifecycle),
    );
    writeSessionSnapshot(window.sessionStorage, CHARLI_SESSION_KEY, standaloneConversationId, snapshot);
  } catch {}
}

function isNoResultsPage() {
  const el = document.querySelector(".nothing-found") || document.querySelector("#chamfr-nothing-found");
    if (!el) return false;
  const style = getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
}

function getSearchBoxQuery() {
  const input = document.querySelector(".search-form-input, .ais-SearchBox-input");
  return input?.value?.trim() || "";
}

function hideNoResultsHeadings(container) {
  const parent = container?.parentElement;
  if (!parent) return;
  parent.querySelectorAll("h1, h2, h3").forEach(el => {
    if (el.textContent?.toLowerCase().includes("no results")) el.style.display = "none";
  });
}

function hideThemeNothingFoundContent(container) {
  Array.from(container.children).forEach(el => {
    if (el?.id === "chamfr-assistant-cta") return;
    if (el?.id === "chamfr-assistant") return;
    el.style.display = "none";
  });
}

function renderAssistantCta(query) {
  const container = document.querySelector(".nothing-found") || document.querySelector("#chamfr-nothing-found");
  if (!container) return;
  if (document.getElementById("chamfr-assistant")) return;

  hideNoResultsHeadings(container);

  const safeQuery = (query || "").trim();
  let cta = document.getElementById("chamfr-assistant-cta");
  if (!cta) {
    cta = document.createElement("div");
    cta.id = "chamfr-assistant-cta";
    cta.style.cssText = "max-width:1400px;margin:0 auto 18px;padding:0 12px;";
    cta.innerHTML = `
      <div style="
        background:linear-gradient(180deg,#fff 0%,#fff 60%,#fff7fb 100%);
        border-radius:16px;padding:18px 18px;color:#0f172a;
        border:1px solid rgba(225,29,115,0.22);
        display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;
      ">
        <div style="display:flex;flex-direction:column;gap:4px;min-width:240px;">
          <div style="font-weight:800;font-size:16px;line-height:1.2;">No results found</div>
          <div style="font-size:13px;color:#475569;">Ask Charli to find close matches or alternatives in-stock.</div>
        </div>
        <div style="display:flex;align-items:center;gap:10px;flex:1;min-width:260px;justify-content:flex-end;">
          <input id="chamfr-charli-query" type="text" style="
            flex:1;max-width:520px;min-width:200px;
            padding:10px 12px;border-radius:12px;border:1px solid rgba(225,29,115,0.22);
            background:#fff;color:#0f172a;font-size:13px;outline:none;
          " />
          <button id="chamfr-chat-with-charli" type="button" style="
            background:${CHAMFR_PINK};color:#fff;border:none;border-radius:12px;
            padding:10px 14px;font-weight:700;font-size:13px;cursor:pointer;
            display:inline-flex;align-items:center;gap:8px;white-space:nowrap;
          ">
            Ask Charli
        </button>
      </div>
    </div>
  `;
    container.prepend(cta);
  }
  cta.dataset.query = safeQuery;
  hideThemeNothingFoundContent(container);
  const input = document.getElementById("chamfr-charli-query");
  if (input && document.activeElement !== input) {
    input.value = safeQuery;
  }
}

function observeNoResults() {
  const target = document.querySelector(".search-results-data") || document.querySelector("#chamfr-products") || document.body;
  const initialQuery = getFailedQuery();
  let submitted = shouldQueueInitialObserverQuery(
    initialQuery,
    !!document.getElementById("chamfr-assistant"),
  );
  let submittedQuery = initialQuery;
  let lastAutoSentQuery = "";
  let pendingAutoQuery = "";
  let unsubscribeAutoSendStatus = null;
  let lastExplicitSubmitQuery = "";
  let lastExplicitSubmitAt = 0;
  let syncingQueryInputs = false;

  const setInputsDisabled = (disabled) => {
    const selectors = [
      "#chamfr-input",
      "#chamfr-send",
      "#chamfr-charli-query",
      "#chamfr-chat-with-charli",
      ".search-form-input",
      ".ais-SearchBox-input",
      ".search-form button[type='submit']",
      ".search-form input[type='submit']",
      ".ais-SearchBox-submit",
      ".ais-SearchBox-reset",
    ];
    document.querySelectorAll(selectors.join(",")).forEach((el) => {
      if (el instanceof HTMLInputElement || el instanceof HTMLButtonElement) {
        el.disabled = !!disabled;
      }
    });
  };

  const getCtaInput = () => document.getElementById("chamfr-charli-query");
  const getCtaRoot = () => document.getElementById("chamfr-assistant-cta");
  const getSearchInput = () => document.querySelector(".search-form-input, .ais-SearchBox-input");

  const syncQueryInputs = (nextQuery, source) => {
    if (syncingQueryInputs) return;
    syncingQueryInputs = true;
    try {
      const q = (nextQuery || "").trim();

      const cta = getCtaRoot();
      if (cta) cta.dataset.query = q;

      const ctaInput = getCtaInput();
      if (ctaInput && ctaInput.value !== q) {
        ctaInput.value = q;
      }

      const searchInput = getSearchInput();
      if (searchInput && searchInput.value !== q) {
        searchInput.value = q;
        if (source === "cta") {
          try {
            searchInput.dispatchEvent(new Event("input", { bubbles: true }));
          } catch {}
        }
      }
    } finally {
      syncingQueryInputs = false;
    }
  };

  const ensureAutoSendListener = () => {
    if (unsubscribeAutoSendStatus) return;
    unsubscribeAutoSendStatus = onStatusUpdated(() => {
      const s = getStatus();
      const busy = s === "streaming" || s === "submitted";
      setInputsDisabled(busy);
      if (!pendingAutoQuery) return;
      if (s !== "ready") return;
      const q = pendingAutoQuery;
      pendingAutoQuery = "";
      requestAssistantSend(q);
    });
  };

  const requestAssistantSend = (query) => {
    const q = (query || "").trim();
    if (!q) return;
    if (!document.getElementById("chamfr-assistant")) return;

    ensureAutoSendListener();

    if (q === lastAutoSentQuery) return;

    const status = getStatus();
    if (status === "streaming" || status === "submitted") {
      return;
    }

    lastAutoSentQuery = q;
    sendText(q);
  };

  const handleExplicitSubmit = (query) => {
    const q = (query || "").trim();
    if (!q) return;

    const s = getStatus();
    if (s === "streaming" || s === "submitted") return;

    const now = Date.now();
    if (q === lastExplicitSubmitQuery && (now - lastExplicitSubmitAt) < 800) return;
    lastExplicitSubmitQuery = q;
    lastExplicitSubmitAt = now;

    submitted = true;
    submittedQuery = q;

    if (isNoResultsPage()) {
      if (document.getElementById("chamfr-assistant")) {
        requestAssistantSend(submittedQuery);
      } else {
        lastAutoSentQuery = submittedQuery;
        setupAssistant(submittedQuery);
      }
      submitted = false;
    }
  };

  const syncCta = (q) => {
    if (!isNoResultsPage()) return;
    if (document.getElementById("chamfr-assistant")) return;
    const query = (q || getSearchBoxQuery() || getFailedQuery()).trim();
    renderAssistantCta(query);
    syncQueryInputs(query, "search");
  };

  document.addEventListener("click", (e) => {
    const btn = e.target?.closest?.("#chamfr-chat-with-charli");
    if (!btn) return;
    const s = getStatus();
    if (s === "streaming" || s === "submitted") return;
    const cta = document.getElementById("chamfr-assistant-cta");
    const ctaInput = document.getElementById("chamfr-charli-query");
    const query = ctaInput?.value?.trim() || cta?.dataset?.query || getSearchBoxQuery() || getFailedQuery();
    setupAssistant(query);
  }, true);

  document.addEventListener("input", (e) => {
    const el = e.target;
    if (!el) return;
    if (!(el instanceof HTMLInputElement)) return;
    if (el.id !== "chamfr-charli-query") return;
    if (!isNoResultsPage()) return;
    if (document.getElementById("chamfr-assistant")) return;
    syncQueryInputs(el.value, "cta");
  }, true);

  document.addEventListener("submit", (e) => {
    const form = e.target;
    if (!form || !(form instanceof HTMLFormElement)) return;
    if (!form.matches(".search-form, .ais-SearchBox-form")) return;
    const input = form.querySelector(".search-form-input, .ais-SearchBox-input");
    handleExplicitSubmit(input?.value);
  }, true);

  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.(
      ".search-form button[type='submit'], .search-form input[type='submit'], .ais-SearchBox-submit, .ais-SearchBox-reset"
    );
    if (!el) return;
    const form = el.closest("form.search-form, form.ais-SearchBox-form");
    const input = form?.querySelector?.(".search-form-input, .ais-SearchBox-input");
    if (input) handleExplicitSubmit(input.value);
  }, true);

  document.addEventListener("input", (e) => {
    const el = e.target;
    if (el && el instanceof HTMLInputElement && el.matches(".search-form-input, .ais-SearchBox-input")) {
      submitted = false;
      submittedQuery = el.value?.trim() || "";
      if (isNoResultsPage() && !document.getElementById("chamfr-assistant")) {
        syncCta(submittedQuery);
      }
    }
  }, true);

  document.addEventListener("keydown", (e) => {
    const el = e.target;
    if (!el || !(el instanceof HTMLInputElement) || !el.matches(".search-form-input, .ais-SearchBox-input")) return;
    if (e.key !== "Enter") return;
    handleExplicitSubmit(el.value);
  }, true);

  const observer = new MutationObserver(() => {
    if (isNoResultsPage()) {
      syncCta(submittedQuery);
    }
    if (submitted && isNoResultsPage()) {
      if (submittedQuery) {
        if (document.getElementById("chamfr-assistant")) requestAssistantSend(submittedQuery);
        else setupAssistant(submittedQuery);
      }
      submitted = false;
    }
  });
  observer.observe(target, { childList: true, subtree: true, attributes: true });
}

function getFailedQuery() {
  const params = new URLSearchParams(location.search);
  return params.get("[query]") || params.get("%5Bquery%5D") || params.get("query") || params.get("s") || "";
}

function setupAssistant(queryOverride, options = {}) {
  const container = document.querySelector(".nothing-found") || document.querySelector("#chamfr-nothing-found");
  if (!container) return;

  hideNoResultsHeadings(container);

  container.innerHTML = `
    <div id="chamfr-assistant" style="max-width:100%;margin:0 auto;padding:0 12px;">
      <div class="chamfr-wrapper" style="
        background:linear-gradient(180deg,#fff 0%,#fff 60%,#fff7fb 100%);
        border-radius:16px;padding:26px;color:#0f172a;
        border:1px solid rgba(225,29,115,0.22);
      ">
        <div class="chamfr-ai-header" style="display:flex;align-items:center;gap:14px;margin-bottom:16px;padding-bottom:14px;border-bottom:1px solid rgba(15,23,42,0.1);">
          <div class="chamfr-ai-header__icon" aria-hidden="true" style="width:52px;height:52px;background:${CHAMFR_PINK};border-radius:999px;display:flex;align-items:center;justify-content:center;flex:0 0 auto;color:#fff;font-size:26px;font-weight:800;line-height:1;">C</div>
          <div style="display:flex;flex-direction:column;gap:2px;min-width:0;">
            <div class="charli-beta-header" style="margin:0;font-size:22px;font-weight:800;color:#0f172a;line-height:1.15;">Charli <span class="charli-beta-anchor" style="font-weight:550;color:#475569;">the Shopping Assistant</span></div>
            <div class="chamfr-ai-header__tagline" style="font-size:14px;color:#64748b;line-height:1.25;">Find close matches and in-stock alternatives</div>
          </div>
          <div class="chamfr-ai-header__phone">
            <span class="chamfr-ai-header__phone-mark" aria-hidden="true">C</span>
            <span class="chamfr-ai-header__phone-title">Charli <span class="chamfr-ai-header__phone-tag">Beta</span></span>
            <span class="chamfr-ai-header__phone-subtitle">Shopping assistant · <span class="chamfr-ai-header__phone-online">online</span></span>
          </div>
          <div class="chamfr-ai-header__status" style="margin-left:auto;display:flex;align-items:center;gap:8px;flex:0 0 auto;">
            <span style="width:9px;height:9px;border-radius:999px;background:#22c55e;display:inline-block;"></span>
            <span class="chamfr-ai-header__status-text" style="font-size:12px;color:#16a34a;font-weight:600;">Online</span>
          </div>
        </div>

        <div id="chamfr-conversation" style="
          display:flex;flex-direction:column;gap:0;min-height:260px;max-height:52vh;
          overflow-y:auto;padding:16px;background:linear-gradient(180deg,rgba(255,255,255,0.92),rgba(255,247,251,0.92));
          border:1px solid rgba(225,29,115,0.16);border-radius:14px;margin-bottom:16px;
        "></div>
        
        <div class="chamfr-ai-composer" style="display:flex;align-items:flex-end;gap:10px;padding:14px 0 0;border-top:1px solid rgba(15,23,42,0.1);">
          <textarea id="chamfr-input" rows="1" placeholder="Ask a follow-up question..." aria-label="Ask Charli a question" style="
            flex:1;padding:10px 18px;border-radius:24px;border:1px solid rgba(225,29,115,0.25);
            background:#fff;color:#0f172a;font-size:16px;line-height:20px;outline:none;
            resize:none;overflow-y:hidden;min-height:42px;box-sizing:border-box;font-family:inherit;
          "></textarea>
          <button id="chamfr-send" style="
            width:42px;height:42px;border:none;border-radius:999px;background:${CHAMFR_PINK};
            color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:0 0 auto;
          ">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
          <button id="chamfr-cancel" style="
            width:42px;height:42px;border:none;border-radius:999px;
            background:${CHAMFR_PINK};color:#fff;cursor:pointer;display:none;align-items:center;justify-content:center;flex:0 0 auto;
          ">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2" /></svg>
        </button>
        </div>
      </div>
    </div>
  `;
  
  const input = document.getElementById("chamfr-input");
  const sendBtn = document.getElementById("chamfr-send");
  const cancelBtn = document.getElementById("chamfr-cancel");
  const conversation = document.getElementById("chamfr-conversation");
  activeRequestLifecycle = null;
  renderedConversationRows = new Map();
  setupConversationAutoScroll(conversation);
  fitStandaloneChatToScreen(conversation);
  scheduleConversationBottomScroll(true);

  onMessagesUpdated(() => {
    noteRequestActivity();
    syncActiveRequestMessages();
    persistStandaloneSession();
    scheduleRenderConversation();
  });
  onStatusUpdated(() => {
    noteRequestActivity();
    const status = getStatus();
    if (status === "streaming" && isRequestActive(activeRequestLifecycle)) {
      activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
        type: "streaming",
        requestId: activeRequestLifecycle.requestId,
      });
    }
    settleToolContinuation(status);
    updateAssistantControls();
    persistStandaloneSession();
    scheduleRenderConversation();
    scheduleConversationBottomScroll();
  });
  onError(event => {
    // Follow-up requests after a browser tool run without a request id.
    const requestId = event?.requestId || awaitingContinuationRequestId;
    if (!requestId) return;
    if (event?.kind === "rate_limit") {
      failActiveRequest(requestId, CHARLI_BUSY_MESSAGE);
      reportRateLimit();
      return;
    }
    failActiveRequest(requestId);
  });

  sendBtn.addEventListener("click", () => handleSend(input));
  // Hardware keyboards: Enter sends, Shift+Enter adds a new line. Touch keyboards: return
  // always adds a new line and the Send button is the only way to send, as in messaging apps.
  const touchKeyboard = window.matchMedia?.("(hover: none) and (pointer: coarse)").matches === true;
  input.setAttribute("enterkeyhint", touchKeyboard ? "enter" : "send");
  input.addEventListener("keydown", e => {
    if (e.key !== "Enter" || e.shiftKey || e.isComposing || touchKeyboard) return;
    e.preventDefault();
    handleSend(input);
  });
  input.addEventListener("input", () => autosizeChatInput(input));
  autosizeChatInput(input);
  cancelBtn.addEventListener("click", () => {
    if (!isRequestActive(activeRequestLifecycle)) return;
    const requestId = activeRequestLifecycle.requestId;
    const supersededAssistantMessageId = activeRequestLifecycle.assistantMessageId;
    activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
      type: "cancel",
      requestId,
      occurredAtMs: performance.now(),
    });
    submitActiveRequestTiming();
    stop();
    const cancellationMessageId = appendAssistantMessage(
      "i got stopped :(",
      supersededAssistantMessageId,
      "cancelled",
    );
    activeRequestLifecycle = {
      ...activeRequestLifecycle,
      assistantMessageId: cancellationMessageId,
    };
    updateAssistantControls();
    scheduleRenderConversation();
  });

  const initialQuery = (queryOverride || getFailedQuery()).trim();
  // Direct visits get a welcome; arrivals that auto-send a query go straight to the answer.
  welcomeEnabled = !(initialQuery && !options.suppressInitialSend);
  if (options.prefillInitialQuery && input) {
    input.value = initialQuery;
    autosizeChatInput(input);
  }
  scheduleRenderConversation();
  if (initialQuery && !options.suppressInitialSend) {
    requestAnimationFrame(() => {
      sendText(initialQuery);
    });
  }
}

let lastSendText = "";
let lastSendAt = 0;
// Safety cap: end a request that has received nothing at all for this long.
const REQUEST_STALL_TIMEOUT_MS = 60000;
let lastRequestActivityAt = 0;
let stallWatchTimer = null;

function noteRequestActivity() {
  lastRequestActivityAt = performance.now();
}

function startStallWatch(requestId) {
  noteRequestActivity();
  if (stallWatchTimer) clearInterval(stallWatchTimer);
  stallWatchTimer = setInterval(() => {
    if (!isRequestActive(activeRequestLifecycle) || activeRequestLifecycle.requestId !== requestId) {
      clearInterval(stallWatchTimer);
      stallWatchTimer = null;
      return;
    }
    if (performance.now() - lastRequestActivityAt < REQUEST_STALL_TIMEOUT_MS) return;
    clearInterval(stallWatchTimer);
    stallWatchTimer = null;
    stop();
    failActiveRequest(requestId);
  }, 5000);
}
function sendText(text, inputToClear = null) {
  if (isRequestActive(activeRequestLifecycle)) return false;
  const message = (text || "").trim();
  if (!message) return false;
  const now = Date.now();
  // Ignore rapid duplicate submits.
  if (message === lastSendText && (now - lastSendAt) < 800) return false;
  lastSendText = message;
  lastSendAt = now;
  if (standaloneSessionEnabled) standaloneSessionWritable = true;
  const requestId = nextRequestId();
  activeRequestLifecycle = createRequestLifecycle(
    requestId,
    getMessages().map(item => item.id),
    performance.now(),
  );
  startStallWatch(requestId);
  scheduleConversationBottomScroll(true);
  scheduleRenderConversation();
  updateAssistantControls();

  if (inputToClear) {
    inputToClear.value = "";
    autosizeChatInput(inputToClear);
  }

  sendMessage(message, requestId)
    .then(() => {
      if (!isRequestActive(activeRequestLifecycle) || activeRequestLifecycle.requestId !== requestId) return;
      if (getStatus() === "error") {
        failActiveRequest(requestId);
        return;
      }
      if (isAwaitingToolContinuation(getMessages())) {
        awaitingContinuationRequestId = requestId;
        return;
      }
      completeActiveRequest(requestId);
    })
    .catch(() => failActiveRequest(requestId));

  return true;
}

// Number questions within the conversation; never reuse a number, even after a failed send.
let lastQuestionNumber = 0;

function nextRequestId() {
  const askedSoFar = getMessages().filter(item => item.role === "user").length;
  lastQuestionNumber = Math.max(lastQuestionNumber, askedSoFar) + 1;
  return createRequestId(getConversationId(), lastQuestionNumber);
}

function completeActiveRequest(requestId) {
  if (!isRequestActive(activeRequestLifecycle) || activeRequestLifecycle.requestId !== requestId) return;
  awaitingContinuationRequestId = null;
  activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
    type: "complete",
    requestId,
    occurredAtMs: performance.now(),
  });
  submitActiveRequestTiming();
  updateAssistantControls();
  persistStandaloneSession();
  scheduleRenderConversation();
}

function settleToolContinuation(status) {
  const requestId = awaitingContinuationRequestId;
  if (!requestId) return;
  if (!isRequestActive(activeRequestLifecycle) || activeRequestLifecycle.requestId !== requestId) {
    awaitingContinuationRequestId = null;
    return;
  }
  if (status === "error") {
    awaitingContinuationRequestId = null;
    failActiveRequest(requestId);
    return;
  }
  if (status === "ready" && !isAwaitingToolContinuation(getMessages())) completeActiveRequest(requestId);
}

const CHAT_INPUT_MAX_HEIGHT = 120;

// Grow the chat textarea with its content up to about five lines, then scroll.
function autosizeChatInput(input) {
  if (!input || input.tagName !== "TEXTAREA") return;
  const before = input.offsetHeight;
  input.style.height = "auto";
  const height = Math.min(input.scrollHeight + 2, CHAT_INPUT_MAX_HEIGHT);
  input.style.height = `${height}px`;
  input.style.overflowY = input.scrollHeight + 2 > CHAT_INPUT_MAX_HEIGHT ? "auto" : "hidden";
  if (input.offsetHeight !== before) fitStandaloneChatToScreen(document.getElementById("chamfr-conversation"));
}

function handleSend(input) {
  sendText(input.value, input);
}

function updateAssistantControls() {
  const sendBtn = document.getElementById("chamfr-send");
  const cancelBtn = document.getElementById("chamfr-cancel");
  const busy = isRequestActive(activeRequestLifecycle);
  if (sendBtn) {
    sendBtn.disabled = busy;
    sendBtn.style.display = busy ? "none" : "flex";
  }
  if (cancelBtn) cancelBtn.style.display = busy ? "flex" : "none";
}

function failActiveRequest(expectedRequestId = null, message = CHARLI_UNAVAILABLE_MESSAGE) {
  if (!isRequestActive(activeRequestLifecycle)) return;
  if (expectedRequestId && activeRequestLifecycle.requestId !== expectedRequestId) return;
  const failedAssistantMessageId = activeRequestLifecycle.assistantMessageId;
  activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
    type: "fail",
    requestId: activeRequestLifecycle.requestId,
    occurredAtMs: performance.now(),
  });
  submitActiveRequestTiming();
  activeRequestLifecycle = {
    ...activeRequestLifecycle,
    assistantMessageId: appendAssistantMessage(
      message,
      failedAssistantMessageId,
      "failed",
    ),
  };
  updateAssistantControls();
  persistStandaloneSession();
  scheduleRenderConversation();
  scheduleConversationBottomScroll();
}

function submitActiveRequestTiming() {
  submitCharliTiming(activeRequestLifecycle, window.algolia?.charliTiming);
}

function syncActiveRequestMessages() {
  if (!isRequestActive(activeRequestLifecycle)) return;
  const messages = getMessages();
  const newMessages = messages.filter(message => !activeRequestLifecycle.initialMessageIds.has(message.id));
  const userMessage = newMessages.find(message => message.role === "user");
  const assistantMessage = newMessages.find(message => message.role === "assistant");

  if (userMessage && !activeRequestLifecycle.userMessageId) {
    activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
      type: "bind-user",
      requestId: activeRequestLifecycle.requestId,
      messageId: userMessage.id,
    });
  }

  if (assistantMessage && !activeRequestLifecycle.assistantMessageId) {
    activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
      type: "bind-assistant",
      requestId: activeRequestLifecycle.requestId,
      messageId: assistantMessage.id,
    });
  }

  if (assistantMessage && hasDisplayableContent(assistantMessage)) {
    activeRequestLifecycle = transitionRequestLifecycle(activeRequestLifecycle, {
      type: "content",
      requestId: activeRequestLifecycle.requestId,
      messageId: assistantMessage.id,
    });
  }
}

function isConversationNearBottom(container) {
  if (!container) return true;
  return container.scrollHeight - container.scrollTop - container.clientHeight <= CONVERSATION_BOTTOM_THRESHOLD;
}

function ensureConversationBottomAnchor(container) {
  let anchor = container.querySelector("#chamfr-conversation-bottom");
  if (!anchor) {
    anchor = document.createElement("div");
    anchor.id = "chamfr-conversation-bottom";
    anchor.setAttribute("aria-hidden", "true");
    anchor.style.cssText = "height:1px;width:100%;flex:0 0 1px;";
  }

  if (anchor.parentElement !== container || anchor !== container.lastElementChild) {
    container.appendChild(anchor);
  }

  return anchor;
}

function scrollConversationToBottom(force = false) {
  const container = document.getElementById("chamfr-conversation");
  if (!container) return;
  if (!force && autoScrollSuppressed) return;

  if (!force && !conversationPinnedToBottom && !isConversationNearBottom(container)) return;

  const anchor = ensureConversationBottomAnchor(container);
  const windowX = window.scrollX;
  const windowY = window.scrollY;

  container.scrollTop = container.scrollHeight;

  try {
    anchor.scrollIntoView({
      block: "end",
      inline: "nearest",
      behavior: "auto",
      container: "nearest",
    });
  } catch {
    container.scrollTop = container.scrollHeight;
  }

  container.scrollTop = container.scrollHeight;
  if (window.scrollX !== windowX || window.scrollY !== windowY) {
    window.scrollTo(windowX, windowY);
  }

  conversationPinnedToBottom = true;
}

function scheduleConversationBottomScroll(force = false) {
  const container = document.getElementById("chamfr-conversation");
  if (!container) return;

  if (force) {
    autoScrollSuppressed = false;
    answerAnchorElement = null;
    conversationPinnedToBottom = true;
    conversationScrollForce = true;
  } else if (autoScrollSuppressed) {
    scheduleAnswerAnchorFollow();
    return;
  } else if (!conversationPinnedToBottom && !isConversationNearBottom(container)) {
    return;
  }

  if (conversationScrollRafId) return;

  conversationScrollRafId = requestAnimationFrame(() => {
    const shouldForce = conversationScrollForce;
    conversationScrollRafId = 0;
    conversationScrollForce = false;
    scrollConversationToBottom(shouldForce);
  });
}

function setupConversationAutoScroll(container) {
  if (!container || container.dataset.autoscrollWired === "1") return;
  container.dataset.autoscrollWired = "1";
  conversationPinnedToBottom = isConversationNearBottom(container);

  // A reader scrolling during an answer takes over; stop following it.
  ["wheel", "touchmove"].forEach(type => container.addEventListener(type, stopAnswerAnchorFollow, { passive: true }));
  container.addEventListener(
    "scroll",
    () => {
      conversationPinnedToBottom = isConversationNearBottom(container);
    },
    { passive: true }
  );
}

function observeConversationContent(container) {
  if (!container || !("ResizeObserver" in window)) return;

  if (conversationResizeObserver) {
    conversationResizeObserver.disconnect();
  }

  conversationResizeObserver = new ResizeObserver(() => {
    scheduleConversationBottomScroll();
  });

  Array.from(container.children).forEach((child) => {
    conversationResizeObserver.observe(child);
  });
}

// Standalone /ai-chat/ page: size the conversation to the space left on screen so the
// input is visible on first load without scrolling, on desktop and phones.
const STANDALONE_CHAT_BOTTOM_GAP = 8;
let standaloneChatFitBound = false;
let standaloneViewport = { width: 0, height: 0 };

// Phone browsers resize the window as the address bar hides and shows while scrolling, and
// Android also when the keyboard opens. Keep the first measured height on touch devices and
// only measure again when the width changes (rotation), so the chat height stays fixed.
function standaloneViewportHeight() {
  const touch = window.matchMedia?.("(hover:none) and (pointer:coarse)").matches;
  if (!touch || !standaloneViewport.height || standaloneViewport.width !== window.innerWidth) {
    standaloneViewport = { width: window.innerWidth, height: window.innerHeight };
  }
  return standaloneViewport.height;
}

function fitStandaloneChatToScreen(conversation) {
  if (!conversation || !document.getElementById("chamfr-ai-chat-page")) return;
  const fit = () => {
    const wrapper = conversation.closest(".chamfr-wrapper");
    if (!wrapper || !conversation.isConnected) return;
    const conversationRect = conversation.getBoundingClientRect();
    const conversationTop = conversationRect.top + window.scrollY;
    const belowConversation = wrapper.getBoundingClientRect().bottom - conversationRect.bottom;
    const available = standaloneViewportHeight() - conversationTop - belowConversation - STANDALONE_CHAT_BOTTOM_GAP;
    const height = Math.max(220, Math.floor(available));
    conversation.style.setProperty("height", `${height}px`, "important");
    conversation.style.setProperty("max-height", "none", "important");
    conversation.style.setProperty("min-height", "0", "important");
  };
  fit();
  if (standaloneChatFitBound) return;
  standaloneChatFitBound = true;
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      fitStandaloneChatToScreen(document.getElementById("chamfr-conversation"));
    });
  };
  // Rotation moves the header for a moment after the resize, so measure again once it settles.
  let settleTimer = 0;
  const scheduleAndSettle = () => {
    schedule();
    clearTimeout(settleTimer);
    settleTimer = setTimeout(schedule, 400);
  };
  window.addEventListener("resize", scheduleAndSettle);
  window.addEventListener("orientationchange", scheduleAndSettle);
  // Header images and fonts can shift the chat after first paint.
  window.addEventListener("load", schedule, { once: true });
  document.fonts?.ready?.then(schedule);
  settleTimer = setTimeout(schedule, 400);
}

function scheduleRenderConversation() {
  renderDirty = true;
  if (document.visibilityState === "hidden") return;
  if (renderRafId) return;

  renderRafId = requestAnimationFrame(() => {
    renderRafId = 0;
    if (!renderDirty) return;
    renderDirty = false;
    renderConversation();
  });
}

document.addEventListener(
  "visibilitychange",
  () => {
    if (document.visibilityState === "visible") scheduleRenderConversation();
  },
  { capture: true }
);

const EMPTY_CHAT_PLACEHOLDER = "Ask Charli about a part, material, or supplier...";
const EMPTY_CHAT_PLACEHOLDER_PHONE = "Ask Charli about a part...";
const FOLLOW_UP_PLACEHOLDER = "Ask a follow-up question...";
const CHAT_INPUT_SHORT_PLACEHOLDER_WIDTH = 440;
let welcomeEnabled = false;

// Display-only greeting for an empty chat. It is never sent to Agent Studio or saved.
function syncWelcome(container, empty) {
  const showWelcome = welcomeEnabled && empty;
  let welcome = container.querySelector(":scope > .chamfr-ai-welcome");
  if (showWelcome && !welcome) {
    welcome = document.createElement("div");
    welcome.className = "chamfr-ai-welcome";
    welcome.innerHTML = `
      <p class="chamfr-ai-welcome__title">Hi, I'm Charli <span class="chamfr-ai-welcome__emoji" aria-hidden="true"><span>👋</span><span>👀</span></span></p>
      <p class="chamfr-ai-welcome__text">I can find in-stock components, compare suppliers, and help you request a quote.</p>
    `;
    container.prepend(welcome);
  } else if (!showWelcome && welcome) {
    welcome.remove();
  }
  const input = document.getElementById("chamfr-input");
  if (input) {
    // Narrow boxes (phones, small windows, tablets in split view) get the short placeholder.
    const narrow = isPhoneLayout() || (input.clientWidth > 0 && input.clientWidth < CHAT_INPUT_SHORT_PLACEHOLDER_WIDTH);
    const emptyPlaceholder = narrow ? EMPTY_CHAT_PLACEHOLDER_PHONE : EMPTY_CHAT_PLACEHOLDER;
    input.placeholder = empty ? emptyPlaceholder : FOLLOW_UP_PLACEHOLDER;
  }
}

function renderConversation() {
  const container = document.getElementById("chamfr-conversation");
  if (!container) return;

  const messages = getMessages();
  const shouldStayPinned = conversationPinnedToBottom || isConversationNearBottom(container);
  const desiredRows = createDesiredRows(messages, activeRequestLifecycle);
  syncWelcome(container, desiredRows.length === 0);
  const existingRows = Array.from(renderedConversationRows.values()).map((row, index) => ({
    key: row.key,
    index,
    fingerprint: row.fingerprint,
    pending: row.pending,
    requestId: row.requestId,
  }));
  const operations = planRowReconciliation(desiredRows, existingRows);

  operations.filter(operation => operation.type === "remove").forEach(operation => {
    const existing = renderedConversationRows.get(operation.key);
    if (existing?.element) stopResponseStatus(existing.element);
    existing?.element.remove();
    renderedConversationRows.delete(operation.key);
  });

  operations.filter(operation => operation.type === "adopt").forEach(operation => {
    const existing = renderedConversationRows.get(operation.fromKey);
    if (!existing) return;
    renderedConversationRows.delete(operation.fromKey);
    existing.key = operation.key;
    existing.pending = false;
    existing.requestId = operation.row.requestId;
    existing.element.dataset.rowKey = operation.key;
    renderedConversationRows.set(operation.key, existing);
  });

  const bottomAnchor = ensureConversationBottomAnchor(container);
  desiredRows.forEach((descriptor, index) => {
    let rendered = renderedConversationRows.get(descriptor.key);
    if (!rendered) {
      rendered = {
        key: descriptor.key,
        fingerprint: "",
        pending: descriptor.pending,
        requestId: descriptor.requestId,
        element: createConversationRow(descriptor.role),
      };
      rendered.element.dataset.rowKey = descriptor.key;
      renderedConversationRows.set(descriptor.key, rendered);
    }

    if (rendered.fingerprint !== descriptor.fingerprint) {
      renderConversationRow(rendered.element, descriptor, index);
      rendered.fingerprint = descriptor.fingerprint;
      rendered.pending = descriptor.pending;
      rendered.requestId = descriptor.requestId;
    }

    const previousRole = index > 0 ? desiredRows[index - 1].role : null;
    rendered.element.style.marginTop = previousRole
      ? (previousRole === descriptor.role ? "6px" : "12px")
      : "0";
  });

  // Only move rows that are out of order; re-inserting a node restarts its CSS animations.
  let nextElement = bottomAnchor;
  for (let index = desiredRows.length - 1; index >= 0; index--) {
    const element = renderedConversationRows.get(desiredRows[index].key)?.element;
    if (!element) continue;
    if (element.parentNode !== container || element.nextSibling !== nextElement) {
      container.insertBefore(element, nextElement);
    }
    nextElement = element;
  }

  const orderedRows = new Map();
  desiredRows.forEach(descriptor => {
    const rendered = renderedConversationRows.get(descriptor.key);
    if (rendered) orderedRows.set(descriptor.key, rendered);
  });
  renderedConversationRows = orderedRows;

  ensureConversationBottomAnchor(container);
  observeConversationContent(container);
  if (autoScrollSuppressed) scheduleAnswerAnchorFollow();
  else scheduleConversationBottomScroll(shouldStayPinned);
}

function createConversationRow(role) {
  const isUser = role === "user";
  const row = document.createElement("div");
  row.className = `chamfr-ai-message-row chamfr-ai-message-row--${isUser ? "user" : "assistant"}`;
  row.style.cssText = `display:flex;gap:${isUser ? "0" : "10px"};align-items:flex-start;width:100%;justify-content:${isUser ? "flex-end" : "flex-start"};margin-top:0;`;

  if (!isUser) {
    const avatar = document.createElement("div");
    avatar.className = "chamfr-ai-avatar";
    avatar.setAttribute("role", "img");
    avatar.setAttribute("aria-label", "Charli");
    // Charli's "C" mark, matching the header and the welcome.
    avatar.style.cssText = `width:30px;height:30px;display:flex;align-items:center;justify-content:center;flex:0 0 auto;border-radius:999px;background:${CHAMFR_PINK};color:#fff;font-size:15px;font-weight:800;line-height:1;`;
    avatar.textContent = "C";
    row.appendChild(avatar);
  }

  const bubble = document.createElement("div");
  bubble.className = `chamfr-ai-message-bubble chamfr-ai-message-bubble--${isUser ? "user" : "assistant"}`;
  bubble.style.cssText = `
    display:inline-block;
    width:fit-content;
    max-width:${isUser ? "min(78%, 720px)" : "min(calc(100% - 40px), 760px)"};
    padding:12px 14px;
    border-radius:${isUser ? "20px 20px 4px 20px" : "4px 18px 18px 18px"};
    font-size:15px;
    line-height:1.6;
    word-break:break-word;
    ${isUser
      ? "background:#f0f0f0;color:#333333;border:none;"
      : "background:#fff1f7;color:#7a1a47;border:1px solid rgba(225,29,115,0.25);"}
  `;
  row.appendChild(bubble);
  return row;
}

function renderConversationRow(row, descriptor, index) {
  const bubble = row.querySelector(".chamfr-ai-message-bubble");
  if (!bubble) return;

  if (descriptor.phase === "failed") {
    clearAnswerReveal(row);
    stopResponseStatus(row);
    bubble.replaceChildren();
    bubble.style.minHeight = "";
    bubble.classList.remove("chamfr-ai-message-bubble--status");
    bubble.classList.remove("chamfr-ai-message-bubble--has-results");
    const failureText = (descriptor.message && extractText(descriptor.message).trim()) || CHARLI_UNAVAILABLE_MESSAGE;
    bubble.append(failureText);
    appendRetryButton(row, bubble, failureText === CHARLI_BUSY_MESSAGE ? RATE_LIMIT_RETRY_DELAY_MS : 0);
    return;
  }

  // A finished answer with no text (tool calls only) gets a fallback instead of endless dots.
  if (!descriptor.pending && descriptor.phase === "completed" && descriptor.message && !hasDisplayableContent(descriptor.message)) {
    renderNoAnswerFallback(row, bubble, descriptor.message);
    return;
  }

  if (descriptor.pending || !hasDisplayableContent(descriptor.message)) {
    ensureResponseStatus(row, bubble, descriptor.progressSteps);
    return;
  }

  // Phones: keep the bubble at its progress height so it does not collapse when text starts.
  if (isPhoneLayout() && bubble.classList.contains("chamfr-ai-message-bubble--status")) {
    const styles = getComputedStyle(bubble);
    const contentHeight = bubble.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
    if (contentHeight > 0) bubble.style.minHeight = `${Math.round(contentHeight)}px`;
  }
  stopResponseStatus(row);
  bubble.classList.remove("chamfr-ai-message-bubble--status");
  if (descriptor.requestId && descriptor.requestId !== anchoredAnswerRequestId) {
    anchoredAnswerRequestId = descriptor.requestId;
    anchorAnswerStart(row);
  }
  const message = descriptor.message;
  const textContent = extractText(message);
  if (renderPhoneStreamingAnswer(row, bubble, descriptor, textContent)) return;
  bubble.style.minHeight = "";
  const textElement = renderMarkdownAnchors(textContent);
  textElement.className = `chamfr-ai-answer${descriptor.phase === "streaming" ? " chamfr-streaming-text" : ""}`;
  const existingText = bubble.querySelector(":scope > .chamfr-ai-answer");
  if (existingText) existingText.replaceWith(textElement);
  else bubble.prepend(textElement);

  if (message.role !== "assistant") return;
  const hits = processCharliHits(extractToolOutputs(message));
  const showResults = shouldShowResults(descriptor.phase, message);
  const animateResultCards = shouldAnimateResultCards(descriptor.phase, descriptor.requestId);
  syncResultCards(bubble, showResults ? hits.products : [], "product", createProductCard, animateResultCards);
  syncResultCards(bubble, showResults ? hits.suppliers : [], "supplier", createSupplierCard, animateResultCards);
  // Size the bubble for cards while text streams so it does not jump wider when they arrive.
  bubble.classList.toggle("chamfr-ai-message-bubble--has-results", hits.products.length > 0 || hits.suppliers.length > 0);

  if (descriptor.phase === "completed" && textContent) {
    if (!bubble.querySelector(":scope > .chamfr-ai-feedback")) {
      const feedback = createFeedbackUI(message, index);
      feedback.classList.add("chamfr-ai-feedback");
      bubble.appendChild(feedback);
    }
  } else {
    bubble.querySelector(":scope > .chamfr-ai-feedback")?.remove();
  }
}

const NO_ANSWER_TEXT = "I couldn't put together an answer for that. Try rephrasing, or ";
const CHARLI_UNAVAILABLE_MESSAGE = "Charli is temporarily unavailable. Please try again later.";
const CHARLI_BUSY_MESSAGE = "Charli is getting a lot of questions right now. Please try again in a minute.";
const RATE_LIMIT_RETRY_DELAY_MS = 60000;

// Tell the site a usage limit was hit so the team can be emailed (at most hourly, server-side).
// Only the reason is sent; no question or answer content.
function reportRateLimit() {
  const endpoint = window.algolia?.charliAlert?.endpoint;
  const nonce = window.algolia?.charliTiming?.nonce;
  if (!endpoint || !nonce) return;
  fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason: "rate_limit", nonce }),
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => {});
}

function renderNoAnswerFallback(row, bubble, message) {
  clearAnswerReveal(row);
  stopResponseStatus(row);
  bubble.style.minHeight = "";
  bubble.classList.remove("chamfr-ai-message-bubble--status");
  bubble.querySelectorAll(":scope > :not(.chamfr-ai-results)").forEach(child => child.remove());
  const text = document.createElement("div");
  text.className = "chamfr-ai-answer";
  const link = document.createElement("a");
  link.href = addCharliAttribution(`${window.location.origin}/rfq/`, window.location.origin);
  link.className = "chamfr-assistant-link";
  link.textContent = "submit an RFQ";
  text.append(NO_ANSWER_TEXT, link, ".");
  bubble.prepend(text);
  const hits = processCharliHits(extractToolOutputs(message));
  syncResultCards(bubble, hits.products, "product", createProductCard, false);
  syncResultCards(bubble, hits.suppliers, "supplier", createSupplierCard, false);
  bubble.classList.toggle("chamfr-ai-message-bubble--has-results", hits.products.length > 0 || hits.suppliers.length > 0);
}

// Only the latest unavailable message offers a retry of the last question.
function appendRetryButton(row, bubble, delayMs = 0) {
  if (!lastSendText) return;
  const wrap = document.createElement("div");
  wrap.className = "chamfr-retry";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "chamfr-retry-btn";
  button.textContent = "Try again";
  // After a usage limit, hold the retry for a minute so shoppers do not keep hitting it.
  if (delayMs > 0) {
    button.disabled = true;
    button.textContent = "Try again in a minute";
    setTimeout(() => {
      button.disabled = false;
      button.textContent = "Try again";
    }, delayMs);
  }
  button.addEventListener("click", () => {
    if (isRequestActive(activeRequestLifecycle)) return;
    wrap.remove();
    lastSendAt = 0;
    sendText(lastSendText);
  });
  wrap.appendChild(button);
  bubble.appendChild(wrap);
  if (row.parentElement) {
    row.parentElement.querySelectorAll(".chamfr-retry").forEach(other => {
      if (other !== wrap) other.remove();
    });
  }
}

// Phones only (CD-1931): reveal streamed answer text at a steady pace, appending words
// to one text node, then swap in the formatted answer and cards once it has all shown.
const phoneLayoutQuery = window.matchMedia?.("(max-width:480px)");
const ANSWER_REVEAL_TICK_MS = 40;
const answerRevealByRow = new WeakMap();

function isPhoneLayout() {
  return phoneLayoutQuery?.matches === true;
}

function renderPhoneStreamingAnswer(row, bubble, descriptor, textContent) {
  const streaming = descriptor.phase === "streaming";
  let reveal = answerRevealByRow.get(row);
  if (!reveal) {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    if (!streaming || !descriptor.requestId || !isPhoneLayout() || reducedMotion) return false;
    reveal = { target: "", shown: 0, done: false, timer: null, element: null };
    answerRevealByRow.set(row, reveal);
  }
  reveal.target = toStreamingDisplayText(textContent);
  reveal.done = !streaming;

  if (!reveal.element?.isConnected) {
    const element = document.createElement("div");
    element.className = "chamfr-ai-answer chamfr-streaming-text";
    element.style.whiteSpace = "pre-wrap";
    element.appendChild(document.createTextNode(""));
    const existing = bubble.querySelector(":scope > .chamfr-ai-answer");
    if (existing) existing.replaceWith(element);
    else bubble.prepend(element);
    reveal.element = element;
    reveal.shown = 0;
  }
  bubble.querySelector(":scope > .chamfr-ai-feedback")?.remove();
  if (!reveal.timer) {
    reveal.timer = setInterval(() => tickAnswerReveal(row, reveal), ANSWER_REVEAL_TICK_MS);
  }
  return true;
}

function tickAnswerReveal(row, reveal) {
  const node = reveal.element?.firstChild;
  if (!node || !reveal.element.isConnected) {
    clearAnswerReveal(row);
    return;
  }
  if (!reveal.target.startsWith(node.data)) {
    let common = 0;
    while (common < node.data.length && node.data[common] === reveal.target[common]) common++;
    node.data = node.data.slice(0, common);
    reveal.shown = common;
  }
  if (reveal.shown < reveal.target.length) {
    // Background tabs throttle timers; show everything rather than crawl when the reader returns.
    const next = document.visibilityState === "hidden"
      ? reveal.target.length
      : nextRevealLength(reveal.shown, reveal.target);
    node.appendData(reveal.target.slice(reveal.shown, next));
    reveal.shown = next;
    scheduleConversationBottomScroll();
    return;
  }
  clearInterval(reveal.timer);
  reveal.timer = null;
  if (!reveal.done) return;
  answerRevealByRow.delete(row);
  // Re-render normally so links, cards, and feedback appear.
  const rendered = renderedConversationRows.get(row.dataset.rowKey);
  if (rendered) rendered.fingerprint = "";
  scheduleRenderConversation();
}

function clearAnswerReveal(row) {
  const reveal = answerRevealByRow.get(row);
  if (reveal?.timer) clearInterval(reveal.timer);
  answerRevealByRow.delete(row);
}

const PROGRESS_DELAY_MS = 300;
const PREPARING_AFTER_IDLE_MS = 1500;
const STILL_WORKING_AFTER_MS = 10000;
const progressStateByRow = new WeakMap();

// When an answer starts, follow it only until the question reaches the top, then stop.
function anchorAnswerStart(row) {
  autoScrollSuppressed = true;
  if (conversationScrollRafId) {
    cancelAnimationFrame(conversationScrollRafId);
    conversationScrollRafId = 0;
  }
  const previous = row.previousElementSibling;
  answerAnchorElement = previous?.classList.contains("chamfr-ai-message-row--user") ? previous : row;
  scheduleAnswerAnchorFollow();
}

function scheduleAnswerAnchorFollow() {
  if (!answerAnchorElement || answerAnchorRafId) return;
  answerAnchorRafId = requestAnimationFrame(() => {
    answerAnchorRafId = 0;
    const container = document.getElementById("chamfr-conversation");
    if (!container || !answerAnchorElement?.isConnected) {
      answerAnchorElement = null;
      return;
    }
    const offset = answerAnchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top - 12;
    const room = container.scrollHeight - container.clientHeight - container.scrollTop;
    if (offset > 0 && room > 0) container.scrollTop += Math.min(offset, room);
  });
}

function stopAnswerAnchorFollow() {
  answerAnchorElement = null;
}

function ensureResponseStatus(row, bubble, steps = []) {
  row.classList.add("chamfr-ai-message-row--thinking");
  let status = bubble.querySelector(":scope > .chamfr-response-status");
  if (!status) {
    bubble.replaceChildren();
    bubble.classList.add("chamfr-ai-message-bubble--status");
    status = document.createElement("div");
    status.className = "chamfr-response-status";
    // Steps sit inside the pink bubble, under the dots.
    status.innerHTML = `
      <span class="chamfr-response-status__dots" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
      <ul class="chamfr-response-steps" aria-hidden="true"></ul>
      <span class="chamfr-response-status__announcer" role="status"></span>
    `;
    bubble.appendChild(status);
  }

  const now = performance.now();
  let progress = progressStateByRow.get(row);
  if (!progress) {
    progress = { createdAt: now, idleSince: now, steps: [], carried: [], lastSteps: [], timer: null, announced: "" };
    progressStateByRow.set(row, progress);
  }
  // A client-tool continuation can rebuild the message without its earlier tool parts; keep those lines ticked.
  if (steps.length < progress.lastSteps.length) {
    progress.carried = [...progress.carried, ...progress.lastSteps.map(step => ({ ...step, done: true }))];
  }
  progress.lastSteps = steps;
  const running = steps.some(step => !step.done);
  if (running) progress.idleSince = null;
  else if (progress.idleSince === null) progress.idleSince = now;
  progress.steps = [...progress.carried, ...steps];
  renderProgress(row, progress);
}

function renderProgress(row, progress) {
  if (progress.timer) clearTimeout(progress.timer);
  progress.timer = null;
  const list = row.querySelector(".chamfr-response-steps");
  const announcer = row.querySelector(".chamfr-response-status__announcer");
  if (!list || !announcer) return;

  const now = performance.now();
  const deadlines = [];
  const items = [];
  const elapsed = now - progress.createdAt;

  if (elapsed < PROGRESS_DELAY_MS) {
    deadlines.push(PROGRESS_DELAY_MS - elapsed);
  } else {
    items.push(...progress.steps);
    if (progress.idleSince !== null) {
      const idleFor = now - progress.idleSince;
      // Always keep one active line while nothing is running.
      if (progress.steps.length && idleFor >= PREPARING_AFTER_IDLE_MS) {
        items.push({ label: PREPARING_LABEL, done: false });
      } else {
        items.push({ label: WORKING_LABEL, done: false });
        if (progress.steps.length) deadlines.push(PREPARING_AFTER_IDLE_MS - idleFor);
      }
    }
    if (elapsed >= STILL_WORKING_AFTER_MS) items.push({ label: STILL_WORKING_LABEL, note: true });
    else deadlines.push(STILL_WORKING_AFTER_MS - elapsed);
  }

  syncProgressItems(list, items);

  // Announce only the newest unfinished step, not the whole list.
  const current = [...items].reverse().find(item => !item.done && !item.note);
  const announcement = current?.label || "";
  if (announcement && announcement !== progress.announced) {
    announcer.textContent = announcement;
    progress.announced = announcement;
  }

  if (deadlines.length) {
    progress.timer = setTimeout(() => renderProgress(row, progress), Math.min(...deadlines) + 10);
  }
}

const PROGRESS_CHECK_ICON = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Reuse existing lines so only newly added steps fade in.
function syncProgressItems(list, items) {
  items.forEach((item, index) => {
    const state = item.note ? "note" : (item.failed ? "failed" : (item.done ? "done" : "active"));
    let entry = list.children[index];
    if (!entry) {
      entry = document.createElement("li");
      entry.innerHTML = '<span class="chamfr-response-steps__mark"></span><span class="chamfr-response-steps__body"><span class="chamfr-response-steps__text"></span><span class="chamfr-response-steps__detail"></span></span>';
      list.appendChild(entry);
    }
    if (entry.dataset.state !== state) {
      entry.dataset.state = state;
      entry.className = `chamfr-response-steps__item is-${state}`;
      const mark = entry.querySelector(".chamfr-response-steps__mark");
      if (state === "done") mark.innerHTML = PROGRESS_CHECK_ICON;
      else if (state === "failed") mark.textContent = "–";
      else if (state === "active") mark.innerHTML = '<span class="chamfr-response-steps__spinner"></span>';
      else mark.replaceChildren();
    }
    const text = entry.querySelector(".chamfr-response-steps__text");
    if (text.textContent !== item.label) text.textContent = item.label;
    typeProgressDetail(entry.querySelector(".chamfr-response-steps__detail"), item.detail || "");
  });
  while (list.children.length > items.length) list.lastElementChild.remove();
}

const PROGRESS_DETAIL_CHARS_PER_TICK = 2;
const PROGRESS_DETAIL_TICK_MS = 30;

// Reveal the intent text at a readable pace; it arrives from Agent Studio in well under a second.
function typeProgressDetail(detail, target) {
  if (!detail || detail.dataset.target === target) return;
  detail.dataset.target = target;
  if (!target.startsWith(detail.textContent)) detail.textContent = "";
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    detail.textContent = target;
    return;
  }
  if (detail.typingTimer) return;
  detail.typingTimer = setInterval(() => {
    const goal = detail.dataset.target || "";
    const current = detail.textContent;
    if (!detail.isConnected || current.length >= goal.length) {
      clearInterval(detail.typingTimer);
      detail.typingTimer = null;
      if (detail.isConnected) detail.textContent = goal;
      return;
    }
    detail.textContent = goal.slice(0, current.length + PROGRESS_DETAIL_CHARS_PER_TICK);
  }, PROGRESS_DETAIL_TICK_MS);
}

function stopResponseStatus(row) {
  const progress = progressStateByRow.get(row);
  if (progress?.timer) clearTimeout(progress.timer);
  progressStateByRow.delete(row);
  row.classList.remove("chamfr-ai-message-row--thinking");
  row.querySelector(".chamfr-response-status")?.remove();
}

function syncResultCards(bubble, results, type, createCard, animateNewCards) {
  const { className, label } = getResultSectionConfig(type);
  let container = bubble.querySelector(`:scope > .${className}`);
  if (!results.length) {
    container?.remove();
    return;
  }
  if (!container) {
    container = document.createElement("div");
    container.className = `chamfr-ai-results ${className}`;
    container.style.cssText = "margin-top:12px;display:flex;flex-direction:column;gap:10px;overflow:visible;width:100%;max-width:100%;box-sizing:border-box;";
    container.setAttribute("role", "group");
    container.setAttribute("aria-label", label);
    bubble.appendChild(container);
  }

  const headings = Array.from(container.querySelectorAll(":scope > .chamfr-ai-results__heading"));
  const heading = headings.shift() || document.createElement("h3");
  heading.className = "chamfr-ai-results__heading";
  heading.textContent = label;
  headings.forEach(duplicate => duplicate.remove());
  if (container.firstElementChild !== heading) container.prepend(heading);

  const existingCards = new Map(
    Array.from(container.querySelectorAll(":scope > [data-result-key]"))
      .map(card => [card.dataset.resultKey, card]),
  );
  const plan = planResultCardReconciliation(results, Array.from(existingCards.keys()), type);
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  plan.remove.forEach(key => existingCards.get(key)?.remove());
  let previousCard = container.querySelector(":scope > .chamfr-ai-results__heading");
  plan.desired.forEach(item => {
    let card = existingCards.get(item.key);
    if (!card) {
      card = createCard(item.result);
      card.dataset.resultKey = item.key;
      const reveal = getResultCardReveal(item.index, animateNewCards, prefersReducedMotion);
      if (reveal) {
        card.classList.add("chamfr-ai-result-card--entering");
        card.style.animationDelay = `${reveal.delay}ms`;
        card.addEventListener("animationend", () => {
          card.classList.remove("chamfr-ai-result-card--entering");
          card.style.removeProperty("animation-delay");
        }, { once: true });
      }
    }
    // Only move cards that are out of order; re-inserting a node restarts its fade-in.
    const expectedPrevious = previousCard || null;
    if (card.parentNode !== container || card.previousElementSibling !== expectedPrevious) {
      if (expectedPrevious) expectedPrevious.after(card);
      else container.prepend(card);
    }
    previousCard = card;
  });
}

function extractText(msg) {
  if (!msg.parts) return "";
  return msg.parts
    .filter(p => p.type === "text")
    .map(p => p.text || "")
    .join("")
    .trim();
}

function renderMarkdownAnchors(text) {
  const el = document.createElement("div");
  el.style.whiteSpace = "pre-wrap";

  const pattern = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    const [raw, label, href] = match;

    el.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));

    let safeHref = "";
    try {
      const url = new URL(href, location.origin);
      if (["http:", "https:"].includes(url.protocol)) {
        safeHref = addCharliAttribution(href, location.origin);
      } else if (url.protocol === "mailto:") {
        safeHref = url.href;
      }
    } catch {
      // invalid URL, skip
    }

    if (safeHref) {
      const link = document.createElement("a");
      link.textContent = label;
      link.href = safeHref;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.className = "chamfr-assistant-link";
      el.appendChild(link);
    } else {
      el.appendChild(document.createTextNode(raw));
    }

    lastIndex = match.index + raw.length;
  }

  el.appendChild(document.createTextNode(text.slice(lastIndex)));
  return el;
}

function extractToolOutputs(msg) {
  if (!msg.parts) return [];
  const hits = [];

  msg.parts.forEach(p => {
    const isToolPart = p.type?.startsWith("tool-") || p.type === "tool-invocation" || p.toolName;
    if (!isToolPart) return;

    const data = p.output || p.result || p.toolResult || {};

    if (data.hits) {
      hits.push(...data.hits);
    } else if (Array.isArray(data) && data.length > 0 && data[0]?.objectID) {
      hits.push(...data);
    } else if (data.results?.[0]?.hits) {
      hits.push(...data.results[0].hits);
    }
  });
  return hits;
}

function createProductCard(p) {
  const card = document.createElement("div");
  card.className = "chamfr-ai-result-card chamfr-ai-result-card--product";
  card.style.cssText = `
    background:linear-gradient(180deg,#fff,#fff6fb);border:1px solid rgba(225,29,115,0.18);
    border-radius:10px;padding:12px 14px;display:flex;gap:12px;align-items:center;
    width:100%;max-width:100%;min-width:0;box-sizing:border-box;
  `;

  if (p.image) {
    const img = document.createElement("img");
    img.className = "chamfr-ai-result-card__image";
    img.src = p.image;
    img.alt = p.name;
    img.loading = "lazy";
    img.decoding = "async";
    img.style.cssText = "width:50px;height:50px;object-fit:contain;border-radius:8px;background:#fff;border:1px solid #e5e7eb;";
    img.addEventListener("load", () => scheduleConversationBottomScroll(), { once: true });
    img.addEventListener("error", () => scheduleConversationBottomScroll(), { once: true });
    card.appendChild(img);
  }

  const info = document.createElement("div");
  info.className = "chamfr-ai-result-card__body";
  info.style.cssText = "flex:1;min-width:0;";
  info.innerHTML = `
    <div class="chamfr-ai-result-card__title" style="font-weight:600;font-size:14px;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(p.name)}</div>
    <div class="chamfr-ai-result-card__price" style="font-size:15px;font-weight:600;color:${CHAMFR_PINK};">${formatPrice(p.price)}</div>
    ${p.seller ? `<div class="chamfr-ai-result-card__meta" style="font-size:12px;color:#999999;">Seller: ${escapeHtml(p.seller)}</div>` : ""}
  `;
  card.appendChild(info);

  if (p.link) {
    const btn = document.createElement("a");
    btn.className = "chamfr-ai-result-card__action";
    btn.href = addCharliAttribution(p.link, location.origin);
    btn.target = "_blank";
    btn.rel = "noopener";
    btn.textContent = "View Product";
    btn.style.cssText = `background:${CHAMFR_PINK};color:#fff;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;white-space:nowrap;`;
    card.appendChild(btn);
  }

  return card;
}

function createSupplierCard(s) {
  const card = document.createElement("div");
  card.className = "chamfr-ai-result-card chamfr-ai-result-card--supplier";
  card.style.cssText = `
    background:linear-gradient(180deg,#fff,#fff6fb);border:1px solid rgba(225,29,115,0.18);
    border-radius:10px;padding:12px 14px;display:flex;gap:12px;align-items:center;
    width:100%;max-width:100%;min-width:0;box-sizing:border-box;
  `;

  if (s.logo) {
    const img = document.createElement("img");
    img.className = "chamfr-ai-result-card__image";
    img.src = s.logo;
    img.alt = s.title;
    img.loading = "lazy";
    img.decoding = "async";
    img.style.cssText = "width:50px;height:50px;object-fit:contain;border-radius:8px;background:#fff;border:1px solid #e5e7eb;";
    img.addEventListener("load", () => scheduleConversationBottomScroll(), { once: true });
    img.addEventListener("error", () => scheduleConversationBottomScroll(), { once: true });
    card.appendChild(img);
  }

  const info = document.createElement("div");
  info.className = "chamfr-ai-result-card__body";
  info.style.cssText = "flex:1;min-width:0;";
  info.innerHTML = `
    <div class="chamfr-ai-result-card__title" style="font-weight:600;font-size:14px;color:#0f172a;">${escapeHtml(s.title)}</div>
    ${s.description ? `<div class="chamfr-ai-result-card__meta" style="font-size:11px;color:#64748b;margin-top:2px;">${escapeHtml(s.description.slice(0, 60))}...</div>` : ""}
  `;
  card.appendChild(info);

  if (s.link) {
    const btn = document.createElement("a");
    btn.className = "chamfr-ai-result-card__action";
    btn.href = addCharliAttribution(s.link, location.origin);
    btn.target = "_blank";
    btn.rel = "noopener";
    btn.textContent = "View Supplier";
    btn.style.cssText = `background:${CHAMFR_PINK};color:#fff;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;white-space:nowrap;`;
    card.appendChild(btn);
  }

  return card;
}

function createFeedbackUI(msg, idx) {
  const div = document.createElement("div");
  div.style.cssText = "display:flex;align-items:center;gap:8px;margin-top:12px;padding-top:10px;border-top:1px solid rgba(0,0,0,0.08);";

  const messageId = msg?.id || "";
  const feedbackKey = `chamfr-feedback-${getChatId()}-${messageId}`;
  let existing = null;
  try {
    existing = localStorage.getItem(feedbackKey);
  } catch {}
  const state = messageId ? feedbackStateByMessageId.get(messageId) : null;

  const renderThanks = (animateOnce) => {
    if (animateOnce) div.classList.add("chamfr-feedback-thanks");
    div.innerHTML = `
      <span style="font-size:12px;color:#64748b;display:inline-flex;align-items:center;gap:6px;">
        <span style="width:8px;height:8px;border-radius:999px;background:${CHAMFR_PINK};display:inline-block;"></span>
        Thanks for your feedback!
      </span>
    `;
    return div;
  };

  if (existing) return renderThanks(false);
  if (state?.status === "done") {
    const animateOnce = !state.renderedDone;
    if (animateOnce) state.renderedDone = true;
    return renderThanks(animateOnce);
  }

  div.innerHTML = `<span style="font-size:11px;color:#94a3b8;">Was this helpful?</span>`;

  const sendFeedback = async (vote) => {
    const appId = window.algolia?.application_id;
    const apiKey = window.algolia?.search_api_key;
    const agentId = window.algolia?.agent_id;

    if (!appId || !apiKey || !agentId || !messageId) {
      throw new Error("Feedback isn't configured (missing app/agent id).");
    }

    const res = await fetch(`https://${appId}.algolia.net/agent-studio/1/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Algolia-Application-Id": appId,
        "X-Algolia-API-Key": apiKey,
      },
      body: JSON.stringify({
        messageId,
        agentId,
        vote,
      }),
    });

    if (res.ok) return;

    if (res.status === 400) return;

    let detail = "";
    try {
      const data = await res.json();
      detail = data?.message || data?.error || "";
    } catch {}
    throw new Error(detail || `Feedback request failed (${res.status}).`);
  };

  const createBtn = (icon, value, color) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.innerHTML = icon;
    btn.className = "chamfr-feedback-btn";
    btn.setAttribute("aria-label", value === "up" ? "Thumbs up" : "Thumbs down");
    btn.setAttribute("aria-pressed", "false");
    btn.style.cssText = `align-items:center;background:none;border:1px solid #e2e8f0;border-radius:8px;color:#64748b;cursor:pointer;display:inline-flex;height:40px;justify-content:center;padding:0;touch-action:manipulation;width:40px;`;

    const isSending = state?.status === "sending";
    const isSelected = state?.vote === value;
    if (isSending) {
      btn.disabled = true;
      if (isSelected) btn.classList.add("chamfr-feedback-btn--loading");
    }
    if (isSelected && (state?.status === "sending" || state?.status === "selected")) {
      btn.classList.add("chamfr-feedback-btn--selected");
      btn.setAttribute("aria-pressed", "true");
      btn.style.borderColor = color;
      btn.style.color = color;
      btn.style.background = `${color}12`;
    }

    btn.onclick = async () => {
      if (btn.disabled) return;
      try {
        if (localStorage.getItem(feedbackKey)) return;
      } catch {}

      const buttons = Array.from(div.querySelectorAll("button"));
      buttons.forEach(b => (b.disabled = true));
      btn.classList.add("chamfr-feedback-btn--loading");
      btn.setAttribute("aria-pressed", "true");
      btn.style.borderColor = color;
      btn.style.color = color;
      btn.style.background = `${color}12`;

      try {
        if (messageId) {
          feedbackStateByMessageId.set(messageId, { status: "sending", vote: value });
        }
        await sendFeedback(value === "up" ? 1 : 0);
        try {
          localStorage.setItem(feedbackKey, value);
        } catch {}
        if (messageId) {
          feedbackStateByMessageId.set(messageId, { status: "done", renderedDone: true });
        }
        renderThanks(true);
      } catch (err) {
        const msgText = (err && typeof err === "object" && "message" in err) ? String(err.message) : "Failed to send feedback.";
        if (messageId) {
          feedbackStateByMessageId.set(messageId, { status: "error", message: msgText, renderedError: false });
        }
        buttons.forEach(b => (b.disabled = false));
        btn.classList.remove("chamfr-feedback-btn--loading");
        div.classList.add("chamfr-feedback-error");
        const safe = escapeHtml(msgText || "Failed to send feedback.");
        const hint = `<span style="font-size:12px;color:#b91c1c;">${safe}</span>`;
        const first = div.querySelector("span");
        if (first) first.insertAdjacentHTML("afterend", hint);
      }
    };
    return btn;
  };

  if (state?.status === "error") {
    const animateOnce = !state.renderedError;
    if (animateOnce) state.renderedError = true;
    div.classList.add("chamfr-feedback-error");
    const safe = escapeHtml(state.message || "Failed to send feedback.");
    div.innerHTML = `<span style="font-size:11px;color:#94a3b8;">Was this helpful?</span><span style="font-size:12px;color:#b91c1c;">${safe}</span>`;
  }

  div.appendChild(createBtn(`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>`, "up", "#22c55e"));
  div.appendChild(createBtn(`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>`, "down", "#ef4444"));

  return div;
}

function extractObjectIDs(msg) {
  const ids = [];
  if (msg.parts) {
    msg.parts.forEach(p => {
      if (p.type === "tool-invocation" && p.result?.hits) {
        p.result.hits.forEach(h => { if (h.objectID) ids.push(h.objectID); });
      }
    });
  }
  return ids;
}

function formatPrice(v) {
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(n)) return "Price unavailable";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);
}

function escapeHtml(s) {
  const div = document.createElement("div");
  div.textContent = s || "";
  return div.innerHTML;
}

function injectStyles() {
  if (document.getElementById("chamfr-ai-styles")) return;
  const style = document.createElement("style");
  style.id = "chamfr-ai-styles";
  style.textContent = `
    @keyframes chamfr-fade-in { from { opacity:0;transform:translateY(10px); } to { opacity:1;transform:translateY(0); } }
    #chamfr-assistant { animation: chamfr-fade-in 0.3s ease-out; }
    .chamfr-response-status {
      align-items:flex-start;
      display:flex;
      flex-direction:column;
      gap:6px;
      justify-content:center;
      min-height:24px;
      min-width:20px;
    }
    .chamfr-ai-message-bubble--status:has(.chamfr-response-steps:not(:empty)) {
      padding:8px 14px 10px !important;
    }
    .chamfr-response-steps {
      color:#8a8a8a;
      font-size:13px;
      line-height:1.5;
      list-style:none;
      margin:0;
      padding:0;
    }
    .chamfr-response-steps:empty {
      display:none;
    }
    .chamfr-response-steps__item {
      align-items:center;
      animation:chamfr-step-in 150ms ease-out;
      display:flex;
      gap:8px;
      margin:2px 0;
    }
    .chamfr-response-steps__mark {
      align-items:center;
      color:#e11d73;
      display:inline-flex;
      flex:0 0 13px;
      height:13px;
      justify-content:center;
      width:13px;
    }
    .chamfr-response-steps__spinner {
      animation:chamfr-step-spin 900ms linear infinite;
      border:1.5px solid rgba(225,29,115,0.25);
      border-radius:50%;
      border-top-color:#e11d73;
      box-sizing:border-box;
      display:block;
      height:11px;
      width:11px;
    }
    .chamfr-response-steps__item.is-active .chamfr-response-steps__text {
      animation:chamfr-step-shimmer 2.2s linear infinite;
      background:linear-gradient(90deg, #5f5f5f 40%, #e11d73 50%, #5f5f5f 60%);
      background-clip:text;
      -webkit-background-clip:text;
      background-size:200% 100%;
      color:transparent;
    }
    .chamfr-response-steps__item {
      align-items:flex-start;
    }
    .chamfr-response-steps__mark {
      margin-top:3px;
    }
    .chamfr-response-steps__body {
      display:flex;
      flex-direction:column;
      min-width:0;
    }
    .chamfr-response-steps__detail {
      color:#9a8a92;
      font-size:12px;
      line-height:1.4;
      margin-top:1px;
    }
    .chamfr-response-steps__detail:empty {
      display:none;
    }
    .chamfr-ai-welcome {
      align-items:center;
      display:flex;
      flex-direction:column;
      gap:6px;
      margin:auto;
      max-width:420px;
      padding:24px 16px;
      text-align:center;
    }
    .chamfr-ai-welcome__title {
      color:#0f172a;
      font-size:18px;
      font-weight:700;
      margin:0;
    }
    /* The wave and eyes take turns every second; CSS opacity only, so no script runs. */
    .chamfr-ai-welcome__emoji {
      display:inline-grid;
      vertical-align:-0.1em;
    }
    .chamfr-ai-welcome__emoji > span {
      animation:chamfr-welcome-emoji 2s ease-in-out infinite;
      grid-area:1 / 1;
    }
    .chamfr-ai-welcome__emoji > span + span {
      animation-delay:-1s;
    }
    @keyframes chamfr-welcome-emoji {
      0%, 42% { opacity:1; }
      50%, 92% { opacity:0; }
      100% { opacity:1; }
    }
    @media (prefers-reduced-motion:reduce) {
      .chamfr-ai-welcome__emoji > span { animation:none; }
      .chamfr-ai-welcome__emoji > span + span { display:none; }
    }
    .chamfr-ai-welcome__text {
      color:#64748b;
      font-size:14px;
      line-height:1.5;
      margin:0;
    }
    .chamfr-retry {
      margin-top:10px;
    }
    .chamfr-retry-btn {
      background:#fff;
      border:1px solid rgba(225,29,115,0.35);
      border-radius:999px;
      color:#be185d;
      cursor:pointer;
      font-size:13px;
      font-weight:600;
      min-height:36px;
      padding:6px 16px;
    }
    .chamfr-retry-btn:disabled {
      cursor:default;
      opacity:0.55;
    }
    .chamfr-retry-btn:hover:not(:disabled) {
      background:#fff1f7;
    }
    .chamfr-response-steps__item.is-failed .chamfr-response-steps__mark {
      color:#94a3b8;
      font-weight:700;
    }
    .chamfr-response-steps__item.is-failed .chamfr-response-steps__text {
      color:#94a3b8;
    }
    .chamfr-response-steps__item.is-note .chamfr-response-steps__text {
      font-style:italic;
    }
    @keyframes chamfr-step-in {
      from { opacity:0; transform:translateY(-2px); }
      to { opacity:1; transform:none; }
    }
    @keyframes chamfr-step-spin {
      to { transform:rotate(360deg); }
    }
    @keyframes chamfr-step-shimmer {
      from { background-position:100% 0; }
      to { background-position:-100% 0; }
    }
    .chamfr-ai-header__phone {
      display:none;
    }
    .chamfr-response-status__announcer {
      border:0;
      clip:rect(0 0 0 0);
      height:1px;
      margin:-1px;
      overflow:hidden;
      padding:0;
      position:absolute;
      white-space:nowrap;
      width:1px;
    }
    .chamfr-response-status__dots {
      align-items:center;
      display:inline-flex;
      gap:5px;
      min-height:24px;
    }
    .chamfr-response-status__dots > span {
      animation:chamfr-bubble-dot 1.2s ease-in-out infinite;
      background:${CHAMFR_PINK};
      border-radius:999px;
      height:6px;
      opacity:0.35;
      transform:scale(0.82);
      width:6px;
    }
    .chamfr-response-status__dots > span:nth-child(2) { animation-delay:120ms; }
    .chamfr-response-status__dots > span:nth-child(3) { animation-delay:240ms; }
    @keyframes chamfr-bubble-dot {
      0%, 60%, 100% { opacity:0.35; transform:scale(0.82); }
      30% { opacity:1; transform:scale(1); }
    }
    .chamfr-ai-message-bubble--status {
      background:#fff1f7 !important;
      border-color:rgba(225,29,115,0.25) !important;
      min-height:40px;
      min-width:48px;
      padding:7px 13px !important;
    }
    .chamfr-streaming-text::after {
      animation:chamfr-stream-caret 0.9s steps(1,end) infinite;
      background:${CHAMFR_PINK};
      border-radius:1px;
      content:"";
      display:inline-block;
      height:1.05em;
      margin-left:3px;
      vertical-align:-0.14em;
      width:2px;
    }
    @keyframes chamfr-stream-caret {
      0%, 45% { opacity:1; }
      46%, 100% { opacity:0; }
    }
    .chamfr-ai-result-card--entering {
      animation:chamfr-result-card-enter 160ms ease-out both;
    }
    @keyframes chamfr-result-card-enter {
      from { opacity:0; transform:translateY(8px); }
      to { opacity:1; transform:translateY(0); }
    }
    @media (prefers-reduced-motion:reduce) {
      .chamfr-streaming-text::after,
      .chamfr-ai-result-card--entering,
      .chamfr-response-steps__item,
      .chamfr-response-steps__spinner,
      .chamfr-response-status__dots > span,
      .chamfr-ai-message-row--thinking .chamfr-ai-avatar { animation:none; }
      .chamfr-response-steps__item.is-active .chamfr-response-steps__text {
        animation:none;
        background:none;
        color:#5f5f5f;
      }
    }
    @keyframes chamfr-feedback-fade {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes chamfr-feedback-shake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-3px); }
      40% { transform: translateX(3px); }
      60% { transform: translateX(-2px); }
      80% { transform: translateX(2px); }
    }
    .chamfr-feedback-thanks { animation: chamfr-feedback-fade 200ms ease-out; }
    .chamfr-feedback-error { animation: chamfr-feedback-shake 220ms ease-out; }
    .chamfr-feedback-btn {
      transition: transform 120ms ease, background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease;
    }
    .chamfr-feedback-btn:hover:not([disabled]) {
      transform: translateY(-1px);
      border-color: rgba(225,29,115,0.28);
      background: rgba(225,29,115,0.06);
      box-shadow: 0 6px 16px rgba(15,23,42,0.08);
    }
    .chamfr-feedback-btn:active:not([disabled]) {
      transform: scale(0.96);
    }
    .chamfr-feedback-btn[disabled] {
      opacity: 0.55;
      cursor: not-allowed;
      box-shadow: none;
      transform: none;
    }
    .chamfr-feedback-btn--loading {
      opacity: 0.75;
    }
    .chamfr-feedback-btn svg {
      pointer-events:none;
    }
    .chamfr-feedback-btn:focus-visible {
      box-shadow:0 0 0 3px rgba(225,29,115,0.2);
      outline:2px solid ${CHAMFR_PINK};
      outline-offset:2px;
    }
    :root { --chamfr-chat-font: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "Apple Color Emoji", "Segoe UI Emoji"; }
    #chamfr-assistant, #chamfr-assistant * { font-family: var(--chamfr-chat-font); }
    #chamfr-assistant-cta, #chamfr-assistant-cta * { font-family: var(--chamfr-chat-font); }
    #chamfr-assistant {
      box-sizing: border-box;
      max-width: 100%;
      overflow-x: hidden;
    }
    #chamfr-conversation {
      box-sizing: border-box;
      max-width: 100%;
      overflow-x: hidden;
    }
    #chamfr-conversation * {
      box-sizing: border-box;
    }
    .chamfr-ai-message-row,
    .chamfr-ai-message-bubble,
    .chamfr-ai-results,
    .chamfr-ai-result-card,
    .chamfr-ai-result-card__body {
      min-width: 0;
      max-width: 100%;
    }
    .chamfr-ai-message-bubble,
    .chamfr-ai-result-card {
      overflow-x: hidden;
    }
    .chamfr-ai-results {
      overflow:visible;
    }
    .chamfr-ai-result-card {
      flex:0 0 auto;
    }
    .chamfr-ai-results__heading {
      border-bottom:1px solid rgba(122,26,71,0.18);
      color:#7a1a47;
      font-size:12px;
      font-weight:700;
      letter-spacing:0.04em;
      line-height:1.3;
      margin:0;
      padding:0 0 6px;
      text-transform:uppercase;
    }
    .chamfr-ai-result-card__image {
      flex: 0 0 auto;
      max-width: 100%;
    }
    .chamfr-ai-result-card__body > div,
    .chamfr-ai-result-card__title,
    .chamfr-ai-result-card__meta {
      overflow-wrap: anywhere;
    }
      .chamfr-ai-result-card__action {
        box-sizing: border-box;
        text-align: center;
      }
      .chamfr-assistant-link {
        color: #e11d73;
        font-weight: 700;
        text-decoration: underline;
      }
      .chamfr-assistant-link:hover {
        color: #be185d;
      }
    .chamfr-ai-message-bubble--has-results {
      width:100% !important;
    }
    #chamfr-input:focus { border-color:${CHAMFR_PINK};box-shadow:0 0 0 4px rgba(225,29,115,0.16); }
    /* Keep the placeholder on one line; a wrapped one is cut off in the one-line box. */
    #chamfr-input::placeholder { white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
    #chamfr-charli-query:focus { border-color:${CHAMFR_PINK};box-shadow:0 0 0 4px rgba(225,29,115,0.10); }
    #chamfr-send:hover { background:#be185d; }
    /* Phones: compact, app-like proportions (CD-1931). */
    @media (max-width:480px) {
      #chamfr-assistant { padding:0 !important; }
      #chamfr-assistant .chamfr-wrapper {
        border-radius:14px !important;
        padding:12px 12px calc(12px + env(safe-area-inset-bottom)) !important;
      }
      /* Phone header: centered Chamfr mark, name with a quiet BETA tag, and status line. */
      .chamfr-ai-header {
        border-bottom:1px solid rgba(15,23,42,0.06) !important;
        justify-content:center !important;
        margin-bottom:8px !important;
        padding:2px 0 10px !important;
      }
      .chamfr-ai-header > :not(.chamfr-ai-header__phone) {
        display:none !important;
      }
      .chamfr-ai-header__phone {
        align-items:center;
        display:flex !important;
        flex-direction:column;
        gap:2px;
        line-height:1.25;
        text-align:center;
      }
      .chamfr-ai-header__phone-mark {
        align-items:center;
        background:#e11d73;
        border-radius:999px;
        color:#fff;
        display:flex;
        font-size:19px;
        font-weight:800;
        height:36px;
        justify-content:center;
        line-height:1;
        margin-bottom:3px;
        width:36px;
      }
      .chamfr-ai-header__phone-title {
        align-items:center;
        color:#0f172a;
        display:inline-flex;
        font-size:15px;
        font-weight:700;
        gap:6px;
      }
      .chamfr-ai-header__phone-tag {
        border:1px solid rgba(225,29,115,0.35);
        border-radius:6px;
        color:#9d174d;
        font-size:9px;
        font-weight:700;
        letter-spacing:0.06em;
        line-height:1.4;
        padding:0 5px;
        text-transform:uppercase;
      }
      .chamfr-ai-header__phone-subtitle {
        color:#64748b;
        font-size:12px;
      }
      .chamfr-ai-header__phone-online {
        color:#16a34a;
      }
      #chamfr-conversation {
        border-radius:12px !important;
        margin-bottom:10px !important;
        max-height:300px;
        overflow-x:hidden;
        padding:8px !important;
      }
      .chamfr-ai-composer {
        gap:8px !important;
        padding-top:10px !important;
      }
      #chamfr-input {
        font-size:16px;
        min-height:44px !important;
        min-width:0;
        padding:11px 14px !important;
      }
      #chamfr-send,
      #chamfr-cancel {
        height:44px !important;
        width:44px !important;
      }
      .chamfr-ai-message-row--assistant .chamfr-ai-avatar {
        display:none !important;
      }
      .chamfr-ai-message-bubble--assistant {
        font-size:14px !important;
        line-height:1.55 !important;
        max-width:100% !important;
        padding:9px 11px !important;
      }
      .chamfr-ai-message-bubble--user {
        font-size:14px !important;
        line-height:1.5 !important;
        max-width:85% !important;
        padding:9px 12px !important;
      }
      .chamfr-ai-message-bubble--has-results {
        width:100% !important;
      }
      .chamfr-ai-answer {
        overflow-wrap:anywhere;
      }
      .chamfr-ai-answer table,
      .chamfr-ai-answer pre {
        display:block;
        max-width:100%;
        overflow-x:auto;
      }
      .chamfr-response-steps {
        font-size:12px;
      }
      .chamfr-response-steps__detail {
        -webkit-box-orient:vertical;
        -webkit-line-clamp:2;
        display:-webkit-box !important;
        font-size:11px;
        overflow:hidden;
      }
      .chamfr-ai-results {
        display:flex !important;
        flex-direction:column !important;
        gap:8px !important;
        margin-top:10px !important;
        overflow:visible !important;
      }
      .chamfr-ai-results__heading {
        font-size:11px !important;
        margin:0 0 2px !important;
      }
      .chamfr-ai-result-card {
        align-items:center !important;
        border-radius:10px !important;
        display:flex !important;
        flex-direction:row !important;
        gap:8px !important;
        min-height:0 !important;
        padding:8px 10px !important;
      }
      .chamfr-ai-result-card__image {
        flex:0 0 40px;
        height:40px !important;
        width:40px !important;
      }
      .chamfr-ai-result-card__body {
        flex:1 1 auto;
        min-width:0;
      }
      .chamfr-ai-result-card__title {
        -webkit-box-orient:vertical;
        -webkit-line-clamp:3;
        display:-webkit-box !important;
        font-size:12px !important;
        line-height:1.3 !important;
        overflow:hidden !important;
        white-space:normal !important;
        word-break:break-word;
      }
      .chamfr-ai-result-card__price {
        font-size:13px !important;
      }
      .chamfr-ai-result-card__meta {
        font-size:11px !important;
        line-height:1.3;
        margin-top:1px !important;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
      }
      /* Short "View" label on phones; the full "View Product/Supplier" text stays for screen readers. */
      .chamfr-ai-result-card__action {
        align-items:center;
        border-radius:999px !important;
        display:inline-flex !important;
        flex:0 0 auto;
        font-size:0 !important;
        justify-content:center;
        line-height:1.2;
        min-height:36px;
        padding:6px 14px !important;
        white-space:nowrap !important;
        width:auto;
      }
      .chamfr-ai-result-card__action::after {
        content:"View";
        font-size:12px;
      }
      .chamfr-feedback-btn {
        height:36px !important;
        width:36px !important;
      }
      .chamfr-streaming-text::after {
        animation:chamfr-stream-caret-soft 1.1s ease-in-out infinite;
        height:0.95em;
        opacity:0.7;
        width:1.5px;
      }
    }
    @keyframes chamfr-stream-caret-soft {
      0%, 100% { opacity:0.7; }
      50% { opacity:0.15; }
    }
    @media (prefers-reduced-motion:reduce) {
      .chamfr-streaming-text::after { animation:none !important; }
    }
    /* Phones in landscape: keep most of the height for the conversation. */
    @media (max-height:500px) and (orientation:landscape) {
      .chamfr-ai-header__tagline,
      .chamfr-ai-header__status-text {
        display:none !important;
      }
      .chamfr-ai-header {
        margin-bottom:8px !important;
        padding-bottom:8px !important;
      }
      .chamfr-ai-header__icon {
        height:32px !important;
        width:32px !important;
      }
      .charli-beta-header {
        font-size:16px !important;
      }
    }
  `;
  document.head.appendChild(style);
}
